import SnoozeIcon from "@mui/icons-material/Snooze";
import { Alert, Button, Snackbar } from "@mui/material";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { fetchBannerSnoozes, snoozeBanner } from "../api/banner-snooze";

// Snoozes end at an hour of the night, not when the user reloads: a tab left
// open overnight picks up the expiry on its next poll.
const POLL_INTERVAL_MS = 5 * 60_000;

interface BannerSnoozeState {
	snoozed: ReadonlySet<string>;
	snooze: (bannerKey: string) => void;
}

const BannerSnoozeContext = createContext<BannerSnoozeState | null>(null);

/** Loads the viewer's active snoozes once for every app-wide banner under it. */
export const BannerSnoozeProvider = ({ children }: { children: ReactNode }) => {
	const [snoozed, setSnoozed] = useState<ReadonlySet<string>>(new Set());

	useEffect(() => {
		let cancelled = false;
		const load = () =>
			fetchBannerSnoozes()
				.then((s) => {
					if (!cancelled) setSnoozed(new Set(s.snoozed));
				})
				.catch(() => null);
		load();
		const id = window.setInterval(load, POLL_INTERVAL_MS);
		return () => {
			cancelled = true;
			window.clearInterval(id);
		};
	}, []);

	const [failed, setFailed] = useState(false);

	// Gone on the click, not after the round trip: the click is the decision.
	// A refused snooze brings the banner back and says so, rather than letting it
	// reappear unexplained on the next load.
	const snooze = useCallback((bannerKey: string) => {
		setSnoozed((prev) => new Set(prev).add(bannerKey));
		snoozeBanner(bannerKey).catch(() => {
			setSnoozed((prev) => {
				const next = new Set(prev);
				next.delete(bannerKey);
				return next;
			});
			setFailed(true);
		});
	}, []);

	const value = useMemo(() => ({ snoozed, snooze }), [snoozed, snooze]);
	return (
		<BannerSnoozeContext.Provider value={value}>
			{children}
			<Snackbar
				open={failed}
				autoHideDuration={8000}
				onClose={() => setFailed(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				data-component="banner-snooze-error-snackbar"
			>
				<Alert
					severity="error"
					onClose={() => setFailed(false)}
					data-component="banner-snooze-error"
				>
					Could not snooze the banner, so it stays. Try again in a moment.
				</Alert>
			</Snackbar>
		</BannerSnoozeContext.Provider>
	);
};

const useBannerSnooze = (): BannerSnoozeState => {
	const state = useContext(BannerSnoozeContext);
	if (!state) throw new Error("useBannerSnooze used outside BannerSnoozeProvider");
	return state;
};

/** Whether this user has muted the banner with this key. */
export const useBannerSnoozed = (bannerKey: string): boolean =>
	useBannerSnooze().snoozed.has(bannerKey);

/** The predicate form, for a component that holds several banners. */
export const useIsBannerSnoozed = (): ((bannerKey: string) => boolean) => {
	const { snoozed } = useBannerSnooze();
	return useCallback((bannerKey: string) => snoozed.has(bannerKey), [snoozed]);
};

/** Hides one banner until early tomorrow morning, or until what it says changes:
 * the key carries the banner's state, so new content is a key never snoozed. */
export const SnoozeButton = ({ bannerKey }: { bannerKey: string }) => {
	const { snooze } = useBannerSnooze();
	return (
		<Button
			color="inherit"
			size="small"
			startIcon={<SnoozeIcon />}
			onClick={() => snooze(bannerKey)}
			data-component="banner-snooze-button"
			sx={{ whiteSpace: "nowrap" }}
		>
			Snooze until tomorrow
		</Button>
	);
};
